/* UTILS */
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import { STATIC_DATA_PATHS } from '@/enums/constants';
import type { Currency, Degree, Period, RemotePolicy, VoiceQuestion } from '@/types/enums/enums.types';
import type { JobOrPmAcronymsResponse, Recommendations } from '@/types/jobs/jobs.types';

import { useGlobalStore } from './global';

type InitialState = {
  currencies: Currency[];
  jobsAcronyms: JobOrPmAcronymsResponse;
  degrees: Degree[];
  remotePolicies: RemotePolicy[];
  periods: Period[];
  voiceQuestions: VoiceQuestion[];
  skillRecommendations: Recommendations;
  skillLevels: { [key: string]: { body: string; score_min: number; title: string } };
};

export const useEnumsStore = defineStore('enumsStore', () => {
  const staticDataUrl = useGlobalStore().getStaticDataUrl;
  const initialState: InitialState = {
    currencies: [],
    jobsAcronyms: {},
    degrees: [],
    periods: [],
    skillRecommendations: {},
    remotePolicies: [],
    voiceQuestions: [],
    skillLevels: {}
  };

  // VARIABLES
  const currencies = ref(initialState.currencies);
  const degrees = ref(initialState.degrees);
  const jobsAcronyms = ref(initialState.jobsAcronyms);
  const periods = ref(initialState.periods);
  const skillRecommendations = ref(initialState.skillRecommendations);
  const remotePolicies = ref(initialState.remotePolicies);
  const voiceQuestions = ref(initialState.voiceQuestions);
  const skillLevels = ref(initialState.skillLevels);

  // GETTERS
  const getCurrencies = computed(() => currencies.value);
  const getDegrees = computed(() => degrees.value);
  const getJobsAcronyms = computed(() => jobsAcronyms.value);
  const getPeriods = computed(() => periods.value);
  const getRemotePolicies = computed(() => remotePolicies.value);
  const getSkillRecommendations = computed(() => skillRecommendations.value);
  const getVoiceQuestions = computed(() => voiceQuestions.value);
  const getSkillLevels = computed(() => skillLevels.value);

  // FUNCTIONS
  async function loadCurrencies() {
    if (!currencies.value.length) {
      const response = await fetch(`${staticDataUrl}${STATIC_DATA_PATHS.currencies}`);
      currencies.value = await response.json();
    }
    return currencies.value;
  }
  async function loadJobsAcronyms() {
    if (!Object.entries(jobsAcronyms.value).length) {
      const response = await fetch(`${staticDataUrl}${STATIC_DATA_PATHS.jobs_acronyms}`);
      jobsAcronyms.value = await response.json();
    }
    return jobsAcronyms.value;
  }
  async function loadDegrees() {
    if (!degrees.value.length) {
      const response = await fetch(`${staticDataUrl}${STATIC_DATA_PATHS.degrees}`);
      degrees.value = await response.json();
    }
    return degrees.value;
  }

  async function loadRemotePolicy() {
    if (!remotePolicies.value.length) {
      const response = await fetch(`${staticDataUrl}${STATIC_DATA_PATHS.remote_policies}`);
      remotePolicies.value = await response.json();
    }
    return remotePolicies.value;
  }
  async function loadPeriods() {
    if (!periods.value.length) {
      const response = await fetch(`${staticDataUrl}${STATIC_DATA_PATHS.periods}`);
      periods.value = await response.json();
    }
    return periods.value;
  }

  async function loadVoiceQuestions() {
    if (!voiceQuestions.value.length) {
      const response = await fetch(`${staticDataUrl}${STATIC_DATA_PATHS.voice_questions}`);
      voiceQuestions.value = await response.json();
    }
    return voiceQuestions.value;
  }

  async function loadSkillRecommendations() {
    if (!Object.entries(skillRecommendations.value).length) {
      const response = await fetch(`${staticDataUrl}${STATIC_DATA_PATHS.skill_recommendations}`);
      skillRecommendations.value = await response.json();
    }
    return skillRecommendations.value;
  }

  async function loadSkillLevels() {
    if (!Object.entries(skillLevels.value).length) {
      const response = await fetch(`${staticDataUrl}${STATIC_DATA_PATHS.skill_levels}`);
      skillLevels.value = await response.json();
    }
    return skillLevels.value;
  }

  return {
    getCurrencies,
    getJobsAcronyms,
    getDegrees,
    getRemotePolicies,
    getPeriods,
    getVoiceQuestions,
    getSkillRecommendations,
    getSkillLevels,
    loadDegrees,
    loadCurrencies,
    loadJobsAcronyms,
    loadRemotePolicy,
    loadPeriods,
    loadVoiceQuestions,
    loadSkillRecommendations,
    loadSkillLevels
  };
});
